/**
 * Platform helpers for the desk PWA: install state, Web Push subscription (iOS 16.4+ home-screen app,
 * Android / desktop browsers), app-icon badge, a short chime + vibration for a new offer, and a tiny
 * history router (the desk has four screens; a router library is not worth its weight here).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from './api.ts';

export const isIos = (): boolean =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const pushSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export type PushState = 'unsupported' | 'needs-install' | 'default' | 'denied' | 'granted';

export function pushState(): PushState {
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!pushSupported()) return 'unsupported';
  return Notification.permission as PushState;
}

export async function registerWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

const b64u = (buf: ArrayBuffer | null): string => {
  if (!buf) return '';
  let s = '';
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const fromB64u = (s: string): Uint8Array<ArrayBuffer> => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

/**
 * MUST be called from a tap (iOS asks for permission only in response to a user gesture). Returns the
 * resulting state; the subscription is stored for THIS device only.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return pushState();
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission as PushState;
  const reg = (await registerWorker()) ?? (await navigator.serviceWorker.ready);
  const { publicKey } = await api.get<{ publicKey: string | null }>('/push/key');
  if (!publicKey) throw new Error('NO_PUSH_KEY');
  let sub = await reg.pushManager.getSubscription();
  if (!sub)
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: fromB64u(publicKey),
    });
  await api.post('/push/subscribe', {
    endpoint: sub.endpoint,
    p256dh: b64u(sub.getKey('p256dh')),
    auth: b64u(sub.getKey('auth')),
  });
  return 'granted';
}

/** Re-sends an existing subscription (it can rotate); silent when there is none. */
export async function refreshPush(): Promise<void> {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub)
      await api.post('/push/subscribe', {
        endpoint: sub.endpoint,
        p256dh: b64u(sub.getKey('p256dh')),
        auth: b64u(sub.getKey('auth')),
      });
  } catch {
    /* next start tries again */
  }
}

export function setBadge(n: number): void {
  const nav = navigator as Navigator & {
    setAppBadge?: (n: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (n > 0) void nav.setAppBadge?.(n).catch(() => undefined);
    else void nav.clearAppBadge?.().catch(() => undefined);
  } catch {
    /* not supported */
  }
}

let audio: AudioContext | null = null;
/** Two soft notes + a short vibration for a new offer (only after the first tap: browser rule). */
export function chime(): void {
  try {
    navigator.vibrate?.([60, 40, 60]);
    audio ??= new AudioContext();
    const ctx = audio;
    const now = ctx.currentTime;
    for (const [i, f] of [880, 1320].entries()) {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + i * 0.14);
      g.gain.exponentialRampToValueAtTime(0.12, now + i * 0.14 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.14 + 0.32);
      o.connect(g).connect(ctx.destination);
      o.start(now + i * 0.14);
      o.stop(now + i * 0.14 + 0.34);
    }
  } catch {
    /* sound is a bonus */
  }
}

// ───────── tiny router ─────────
const listeners = new Set<() => void>();
window.addEventListener('popstate', () => listeners.forEach((l) => l()));
export function navigate(path: string, replace = false): void {
  if (path === window.location.pathname) return;
  if (replace) window.history.replaceState(null, '', path);
  else window.history.pushState(null, '', path);
  listeners.forEach((l) => l());
}
export function usePath(): string {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => window.location.pathname,
  );
}

/**
 * iPhone keyboard (owner 2026-10-16): iOS shrinks only the VISUAL viewport when the keyboard opens and
 * scrolls the page under it. The workspace follows the visible area instead (--vvh / --vvt), and the
 * document itself never scrolls — so the composer sits right above the keyboard, nothing jumps.
 * While the keyboard is open the home-indicator inset (--hx-sab) is 0: iOS keeps reporting
 * env(safe-area-inset-bottom) above the keyboard, which left a visible gap under the composer.
 */
export function useVisualViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    const prev = { html: root.style.overflow, body: document.body.style.overflow };
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const fit = () => {
      if (!vv) return;
      root.style.setProperty('--vvh', `${vv.height}px`);
      root.style.setProperty('--vvt', `${vv.offsetTop}px`);
      const keyboard = window.innerHeight - vv.height > 120;
      if (keyboard) root.style.setProperty('--hx-sab', '0px');
      else root.style.removeProperty('--hx-sab');
      root.toggleAttribute('data-keyboard', keyboard);
      // iOS may still pan the layout viewport; the workspace follows --vvt, the document stays at 0.
      if (window.scrollY !== 0 && !keyboard) window.scrollTo(0, 0);
    };
    // The keyboard animates for ~300 ms after focus; settle once more after it.
    const later: number[] = [];
    const onFocus = () => {
      later.push(window.setTimeout(fit, 60), window.setTimeout(fit, 350));
    };
    fit();
    vv?.addEventListener('resize', fit);
    vv?.addEventListener('scroll', fit);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onFocus);
    return () => {
      vv?.removeEventListener('resize', fit);
      vv?.removeEventListener('scroll', fit);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onFocus);
      later.forEach((t) => window.clearTimeout(t));
      root.style.overflow = prev.html;
      document.body.style.overflow = prev.body;
      root.style.removeProperty('--vvh');
      root.style.removeProperty('--vvt');
      root.style.removeProperty('--hx-sab');
      root.removeAttribute('data-keyboard');
    };
  }, []);
}

/** True while the page is visible (polling pauses in the background; push takes over). */
export function useVisible(): boolean {
  const [v, setV] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const f = () => setV(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', f);
    return () => document.removeEventListener('visibilitychange', f);
  }, []);
  return v;
}

/** Messages from the service worker: a push arrived / a notification was tapped. */
export function useWorkerMessages(onPush: () => void): void {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const f = (e: MessageEvent) => {
      const d = e.data as { type?: string; url?: string } | null;
      if (d?.type === 'desk-push') onPush();
      if (d?.type === 'desk-open' && typeof d.url === 'string' && d.url.startsWith('/'))
        navigate(d.url);
    };
    navigator.serviceWorker.addEventListener('message', f);
    return () => navigator.serviceWorker.removeEventListener('message', f);
  }, [onPush]);
}
